import {
  List,
  useTable,
  ShowButton,
  DeleteButton,
  DateField,
} from "@refinedev/antd";
import { useList, type CrudFilters } from "@refinedev/core";
import {
  Table,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Card,
  Input,
  Select,
  Button,
  Tooltip,
  Progress,
} from "antd";
import {
  FlagOutlined,
  ApartmentOutlined,
  TeamOutlined,
  FileTextOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { Brain } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { intFmt } from "@/lib/format";

const { Text } = Typography;

/**
 * /admin/brains — moderação de Brains (contextos compartilhados).
 *
 * Como obtém os dados (MOCK MODE): `useTable` lista paginado contra os fixtures
 * (busca por nome, filtro denunciados, ordenação por membros/entradas). Um
 * `useList({ pagination:{ mode:"off" } })` agrega os KPIs (total, denunciados,
 * Σ membros, Σ entradas). Mesma fonte de GET /admin/brains na API real.
 *
 * Ações de moderação: ver detalhe e excluir (DELETE /admin/brains/:id).
 */
export const BrainList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [state, setState] = useState<"flagged" | "shared" | undefined>();

  const filters = useMemo<CrudFilters>(() => {
    const f: CrudFilters = [];
    if (search.trim()) f.push({ field: "nome", operator: "contains", value: search.trim() });
    if (state === "flagged") f.push({ field: "flagged", operator: "eq", value: true });
    if (state === "shared") f.push({ field: "members_count", operator: "gt", value: 1 });
    return f;
  }, [search, state]);

  const { tableProps } = useTable<Brain>({
    syncWithLocation: true,
    filters: { permanent: filters },
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
  });

  const { data: allData, isLoading: loadingAll } = useList<Brain>({
    resource: "brains",
    pagination: { mode: "off" },
  });
  const all = useMemo(() => allData?.data ?? [], [allData]);
  const kpis = useMemo(() => {
    let flagged = 0;
    let members = 0;
    let entries = 0;
    let shared = 0;
    for (const b of all) {
      if (b.flagged) flagged++;
      members += b.members_count ?? 0;
      entries += b.entries_count ?? 0;
      if ((b.members_count ?? 0) > 1) shared++;
    }
    return { total: all.length, flagged, members, entries, shared };
  }, [all]);

  // Maior contagem de entradas → usada para normalizar a barra na coluna.
  const maxEntries = useMemo(
    () => all.reduce((m, b) => Math.max(m, b.entries_count ?? 0), 0) || 1,
    [all],
  );

  const hasFilters = !!(search || state);

  return (
    <List title="Moderação · Brains">
      {/* KPIs de moderação. */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} lg={6}>
          <StatCard
            title="Brains"
            value={kpis.total}
            loading={loadingAll}
            prefix={<ApartmentOutlined />}
            hint={`${kpis.shared} compartilhados`}
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatCard
            title="Denunciados"
            value={kpis.flagged}
            loading={loadingAll}
            prefix={<FlagOutlined />}
            hint="aguardando revisão"
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatCard
            title="Membros"
            value={kpis.members}
            loading={loadingAll}
            prefix={<TeamOutlined />}
            hint="Σ entre todos os Brains"
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatCard
            title="Entradas"
            value={kpis.entries}
            loading={loadingAll}
            prefix={<FileTextOutlined />}
            hint="Σ entries armazenadas"
          />
        </Col>
      </Row>

      {/* Filtros. */}
      <Card className="mel-glass" bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={12} lg={10}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar por nome do Brain…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col xs={18} md={8} lg={6}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Estado"
              value={state}
              onChange={setState}
              options={[
                { label: "Denunciados", value: "flagged" },
                { label: "Compartilhados", value: "shared" },
              ]}
            />
          </Col>
          <Col xs={6} md={4} lg={2}>
            <Tooltip title="Limpar filtros">
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setSearch("");
                  setState(undefined);
                }}
                disabled={!hasFilters}
                block
              />
            </Tooltip>
          </Col>
        </Row>
      </Card>

      <Card className="mel-glass" bordered={false}>
        <Table
          {...tableProps}
          rowKey="id"
          scroll={{ x: 900 }}
          onRow={(row: Brain) => ({
            style: row.flagged ? { background: "rgba(239,68,68,0.05)" } : undefined,
          })}
        >
          <Table.Column<Brain>
            title="Brain"
            render={(_, row) => (
              <Space>
                <span style={{ fontSize: 18 }}>{row.emoji ?? "🧠"}</span>
                <Space direction="vertical" size={0}>
                  <Text strong>{row.nome}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.id}
                  </Text>
                </Space>
                {row.is_default && <Tag color="cyan">default</Tag>}
              </Space>
            )}
          />
          <Table.Column
            dataIndex="members_count"
            title="Membros"
            sorter
            render={(v?: number) => (
              <Tag color={(v ?? 0) > 1 ? "blue" : "default"} icon={<TeamOutlined />}>
                {v ?? 0}
              </Tag>
            )}
          />
          <Table.Column<Brain>
            title="Entradas"
            dataIndex="entries_count"
            sorter
            render={(v?: number) => (
              <Space direction="vertical" size={2} style={{ minWidth: 120 }}>
                <Text>{intFmt.format(v ?? 0)}</Text>
                <Progress
                  percent={Math.round(((v ?? 0) / maxEntries) * 100)}
                  showInfo={false}
                  size="small"
                  strokeColor="#06B6D4"
                />
              </Space>
            )}
          />
          <Table.Column
            dataIndex="flagged"
            title="Estado"
            render={(f?: boolean) =>
              f ? (
                <Tag color="red" icon={<FlagOutlined />}>
                  denunciado
                </Tag>
              ) : (
                <Tag>ok</Tag>
              )
            }
          />
          <Table.Column
            dataIndex="created_at"
            title="Criado"
            render={(v: string) => <DateField value={v} format="DD/MM/YYYY" />}
          />
          <Table.Column<Brain>
            title="Ações"
            fixed="right"
            width={110}
            render={(_, row) => (
              <Space>
                <ShowButton hideText size="small" recordItemId={row.id} />
                <DeleteButton
                  hideText
                  size="small"
                  recordItemId={row.id}
                  confirmTitle="Remover este Brain por moderação?"
                />
              </Space>
            )}
          />
        </Table>
      </Card>
    </List>
  );
};
